import { useNavigate } from 'react-router';
import { Controller, FormContainer, useFormContext } from 'react-hook-form-mui';
import { JobPostHeader, ScrollableWrapper } from 'view/pages/createJobPost/components';
import { BackButton, Button, Input, RadioGroup, Spinner } from 'view/components';
import TestSelect from 'view/pages/profile/TestPage/Select';
import TestMultiplySelect from 'view/pages/profile/TestPage/MultiplySelect';
import { useGetDropdownData } from 'view/pages/profile/hooks/useGetDropdownData';
import { DropdownsEnum } from 'infrastructure/enums/DropdownsEnum';
import * as CS from 'view/pages/createJobPost/commonStyled';
import SalaryRange from 'view/components/SalaryRange';
import { ArrowIcon } from 'assets';
import { useUpdateJobPostByStep } from 'view/pages/createJobPost/hooks/useUpdateJobPostByStep';
import { useContextSelector as useCS } from 'use-context-selector';
import {
  CreateJobPostConfigContext,
  TCreateJobPostContextState,
} from 'domain/createJobPostConfig/CreateJobPostContext';
import { createJobPostConfigSelectors } from 'domain/createJobPostConfig/selectors';
import { CreateJobPostBackToHomeModal } from 'view/modals';
import { Location, useLocation } from 'react-router-dom';
import { useMediaQuery } from '@mui/material';
import { theme } from 'view/themes/theme';
import { TUpdate } from 'domain/types';
import Step from '@mui/material/Step';
import { steps } from 'view/layouts/CreateJobPostLayout';
import { useStateMachine } from 'little-state-machine';
import { updateStep2Action } from 'view/pages/createJobPost/helpers/updateStep2Action';

const yearsOfExperienceOptions = [
  {
    value: 'Less than 1 year',
    label: 'Less than 1 year',
    experienceYearsMin: 0,
    experienceYearsMax: 1,
  },
  {
    value: '1-2 years',
    label: '1-2 years',
    experienceYearsMin: 1,
    experienceYearsMax: 2,
  },
  { value: '3-5 years', label: '3-5 years', experienceYearsMin: 3, experienceYearsMax: 5 },
  { value: '6-10 years', label: '6-10 years', experienceYearsMin: 6, experienceYearsMax: 10 },
  {
    value: 'More than 10 years',
    label: 'More than 10 years',
    experienceYearsMin: 10,
    experienceYearsMax: 100,
  },
];

interface IFormInput {
  experienceLevel: null;
  salaryRange: { from: string; to: string };
  languages: null;
  education: null;
  rolesAndResponsibilities: string;
  yearsOfExperience: string;
}

const JobDetailsFields = ({
  onDraftSubmit,
  experienceLevelData,
  isExperienceLevelLoading,
  languageData,
  isLanguageDataLoading,
  educationData,
  isEducationDataLoading,
  isUpdating,
}) => {
  const navigate = useNavigate();
  const location: Location = useLocation();
  const matches = useMediaQuery(theme.breakpoints.down('desktop'));
  const update: TUpdate<TCreateJobPostContextState> | undefined = useCS(
    CreateJobPostConfigContext,
    createJobPostConfigSelectors.update
  );
  const createJobPostData = useCS(
    CreateJobPostConfigContext,
    createJobPostConfigSelectors.createJobPostData
  );

  const activeStep = steps.findIndex((step) => {
    return location.pathname.includes(step.path);
  });

  const {
    watch,
    setValue,
    control,
    handleSubmit,
    formState: { errors },
  } = useFormContext<IFormInput>();

  const salaryRange = watch('salaryRange') || { from: '', to: '' };
  const responsibilitiesValue = watch('rolesAndResponsibilities') || '';
  const yearsOfExperienceValue = watch('yearsOfExperience') || '';

  return (
    <>
      <CS.CreateJobPostHeader style={{ justifyContent: 'space-between' }}>
        {matches ? (
          <CS.CJPBackButtonBox>
            <BackButton
              text="Back to Home"
              color="#663497"
              onClick={() => {
                if (update) update({ isOpenBackToHomeModal: true });
              }}
            />
          </CS.CJPBackButtonBox>
        ) : null}
        <CS.CreateJobPostHeader>
          <Button
            variant="outlined"
            text="Save as Draft"
            onClick={handleSubmit(onDraftSubmit)}
            style={{ padding: '8px 20px', width: 'max-content' }}
            disabled={
              !!Object.keys(errors).length ||
              !responsibilitiesValue ||
              !yearsOfExperienceValue ||
              isUpdating
            }
          />
        </CS.CreateJobPostHeader>
      </CS.CreateJobPostHeader>
      {matches ? (
        <div>
          <JobPostHeader
            title={`${createJobPostData?.editStatus ? 'Edit' : 'Create'} Job Post`}
            style={{ marginBottom: '24px', width: 'max-content' }}
          />
          <CS.CustomStepper activeStep={activeStep} orientation="horizontal" alternativeLabel>
            {steps.map((step) => {
              return (
                <Step key={step.label}>
                  <CS.StepLabelText />
                </Step>
              );
            })}
          </CS.CustomStepper>
        </div>
      ) : null}
      <ScrollableWrapper>
        <CS.CreateJobPostMainWrapper>
          <JobPostHeader
            title="Job Details"
            subtitle={
              'Let\u0027s create an amazing job post with a clear description \n and requirements to find the absolute best talent!'
            }
          />

          <Controller
            name="experienceLevel"
            control={control}
            render={({ field }) => {
              return (
                <TestSelect
                  {...field}
                  label="Experience Level Required"
                  options={experienceLevelData || []}
                  selectedValue={field.value}
                  onChange={(selected) => {
                    return field.onChange(selected);
                  }}
                  disabled={isExperienceLevelLoading || !experienceLevelData}
                />
              );
            }}
          />

          <RadioGroup
            name="yearsOfExperience"
            label="Years of Experience *"
            options={yearsOfExperienceOptions}
            style={{ marginBottom: '8px' }}
            styleLabel={{ margin: '0' }}
          />

          <SalaryRange
            fromValue={salaryRange.from}
            toValue={salaryRange.to}
            onChange={(field, value) => {
              setValue('salaryRange', { ...salaryRange, [field]: value });
            }}
          />
          <TestMultiplySelect
            name="languages"
            label="Languages Required"
            options={languageData || []}
            control={control}
            disabled={isLanguageDataLoading || !languageData}
          />
          <Controller
            name="education"
            control={control}
            render={({ field }) => {
              return (
                <TestSelect
                  {...field}
                  label="Education Level"
                  options={educationData || []}
                  selectedValue={field.value}
                  onChange={(selected) => {
                    return field.onChange(selected);
                  }}
                  disabled={isEducationDataLoading || !educationData}
                />
              );
            }}
          />
          <Input
            name="rolesAndResponsibilities"
            label="Roles and Resposibilities *"
            placeholder="Add roles and responsibilities of current position..."
            multiline
            rows={6}
          />

          <CS.CJPSubmitBtnWrapper>
            <Button
              variant="outlined"
              text="Back"
              onClick={() => {
                return navigate('/create-job-post/step1', { replace: true });
              }}
              style={{ margin: '32px 0' }}
            />
            <Button
              variant="contained"
              type="submit"
              text="Next"
              style={{ margin: '32px 0' }}
              endIcon={
                isUpdating ? (
                  <Spinner
                    disabled={!!Object.keys(errors).length || isUpdating}
                    variant="contained"
                  />
                ) : (
                  <ArrowIcon rotate="right" color="white" />
                )
              }
              disabled={
                !!Object.keys(errors).length ||
                !responsibilitiesValue ||
                !yearsOfExperienceValue ||
                isUpdating
              }
            />
          </CS.CJPSubmitBtnWrapper>
        </CS.CreateJobPostMainWrapper>
      </ScrollableWrapper>
      <CreateJobPostBackToHomeModal
        onDraftSubmitClick={handleSubmit(onDraftSubmit)}
        endIcon={
          isUpdating ? (
            <Spinner disabled={!!Object.keys(errors).length || isUpdating} variant="contained" />
          ) : null
        }
        disabled={
          !!Object.keys(errors).length ||
          !responsibilitiesValue ||
          !yearsOfExperienceValue ||
          isUpdating
        }
      />
    </>
  );
};

const JobDetails = () => {
  const navigate = useNavigate();
  const createJobPostData = useCS(
    CreateJobPostConfigContext,
    createJobPostConfigSelectors.createJobPostData
  );
  const { data: experienceLevelData, isLoading: isExperienceLevelLoading } = useGetDropdownData(
    DropdownsEnum.EXPERIENCE_LEVEL
  );
  const { data: languageData, isLoading: isLanguageDataLoading } = useGetDropdownData(
    DropdownsEnum.LANGUAGE
  );
  const { data: educationData, isLoading: isEducationDataLoading } = useGetDropdownData(
    DropdownsEnum.EDUCATION
  );

  const { isLoading: isUpdating, updateJobPostByStep } =
    useUpdateJobPostByStep('/create-job-post/step3');

  const getDataToSubmit = (formData: IFormInput) => {
    const selectedItem: any = formData.yearsOfExperience
      ? yearsOfExperienceOptions.filter((item) => {
          return formData.yearsOfExperience === item.value;
        })[0]
      : null;
    return {
      experienceLevel: formData.experienceLevel,
      experienceYearsMin: selectedItem ? selectedItem.experienceYearsMin : 0,
      experienceYearsMax: selectedItem ? selectedItem.experienceYearsMax : 0,
      salaryMin: Number(formData.salaryRange.from),
      salaryMax: Number(formData.salaryRange.to),
      languages: formData.languages,
      education: formData.education,
      rolesAndResponsibilities: formData.rolesAndResponsibilities,
      progressStep: 3,
    };
  };

  const { state, actions } = useStateMachine({ actions: { updateStep2Action } });

  const onSubmit = (formData: IFormInput) => {
    const dataToSend = getDataToSubmit(formData);
    actions.updateStep2Action(dataToSend);
    navigate('/create-job-post/step3');
    /* updateJobPostByStep(dataToSend); */
  };

  const onDraftSubmit = (formData: IFormInput) => {
    const dataToSend = getDataToSubmit(formData);
    const dataToSendWithoutEmptyValues = JSON.parse(
      JSON.stringify({ ...state.data, ...dataToSend })
    );
    dataToSendWithoutEmptyValues.immHiringNeed =
      dataToSendWithoutEmptyValues?.immHiringNeed?.name === 'Yes';
    for (const key in dataToSendWithoutEmptyValues) {
      if (dataToSendWithoutEmptyValues[key] === null || dataToSendWithoutEmptyValues[key] === '') {
        delete dataToSendWithoutEmptyValues[key];
      }
    }
    updateJobPostByStep(
      { ...dataToSendWithoutEmptyValues, editStatus: null, postStatus: 'draft' },
      true
    );
  };

  const defaultValues = {
    experienceLevel: state.data.experienceLevel || createJobPostData?.experienceLevel || null,
    yearsOfExperience:
      yearsOfExperienceOptions.filter((item) => {
        return (
          item.experienceYearsMin === state.data?.experienceYearsMin &&
          item.experienceYearsMax === state.data?.experienceYearsMax
        );
      })?.[0]?.value ||
      yearsOfExperienceOptions.filter((item) => {
        return (
          item.experienceYearsMin === createJobPostData?.experienceYearsMin &&
          item.experienceYearsMax === createJobPostData?.experienceYearsMax
        );
      })?.[0]?.value ||
      '',
    salaryRange: {
      from: state.data?.salaryMin || createJobPostData?.salaryMin || '',
      to: state.data?.salaryMax || createJobPostData?.salaryMax || '',
    },
    languages: state.data?.languages || createJobPostData?.languages || null,
    education: state.data?.education || createJobPostData?.education || null,
    rolesAndResponsibilities:
      state.data?.rolesAndResponsibilities || createJobPostData?.rolesAndResponsibilities || '',
  };

  return (
    <>
      <FormContainer
        key={JSON.stringify(defaultValues)}
        defaultValues={defaultValues}
        onSuccess={onSubmit}
      >
        <JobDetailsFields
          onDraftSubmit={onDraftSubmit}
          experienceLevelData={experienceLevelData}
          isExperienceLevelLoading={isExperienceLevelLoading}
          languageData={languageData}
          isLanguageDataLoading={isLanguageDataLoading}
          educationData={educationData}
          isEducationDataLoading={isEducationDataLoading}
          isUpdating={isUpdating}
        />
      </FormContainer>
    </>
  );
};

export default JobDetails;
